import useFetch from 'use-http'
import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import dateFormat from 'dateformat'
import { useRouter } from 'next/router'
import Image from 'next/image'

import { Box, Button, Text, Heading, Grid, Image as ThemeImage } from 'theme-ui'

import { apiUrl } from '../../environment'

import Header from '../../components/Header'
import Download from '../../components/pages/art/Download'
import Zoom from '../../components/pages/art/Zoom'
import FullScreenLoader from '../../components/FullScreenLoader'
import FullScreenError from '../../components/FullScreenError'

import OpenSeaIcon from '../../components/icons/OpenSea'
import AtmosphereIcon from '../../components/icons/Atmosphere'
import BodyIcon from '../../components/icons/Body'
import ColorIcon from '../../components/icons/Color'
import EyeIcon from '../../components/icons/Eye'
import FaceIcon from '../../components/icons/Face'
import GlassesIcon from '../../components/icons/Glasses'
import ZoomIcon from '../../components/icons/Zoom'
import HatIcon from '../../components/icons/Hat'
import MouthIcon from '../../components/icons/Mouth'
import RotateIcon from '../../components/icons/Rotate'
import BirthdayIcon from '../../components/icons/Birthday'

import { backgroundTransformer } from '../../components/pages/art/bg-transformer'

let AnimatedBox = motion(Box)

// Framer animations
const duration = 0.3
const flipVariants = {
    shown: {
        rotateY: 0,
        transition: {
            duration,
        },
    },
    frontFlipped: {
        rotateY: -180,
        transition: {
            duration,
        },
    },
    backFlipped: {
        rotateY: 180,
        transition: {
            duration,
        },
    },
}

export default function Art() {
    const router = useRouter()
    const tid = router.query.id

    const [isCardFlipped, setIsCardFlipped] = useState(false)
    const [isZooming, setIsZooming] = useState(false)

    const { get, response, loading, error } = useFetch(`${apiUrl}/token/${tid}`)
    const [token, setToken] = useState(null)

    useEffect(() => {
        if (!!tid) {
            fetchData()
        }
    }, [tid])

    const fetchData = async () => {
        const data = await get()
        if (response.ok) setToken(data)
    }

    if (loading) {
        return <FullScreenLoader />
    }

    if (!token || error) {
        return <FullScreenError />
    }

    if (isZooming) {
        return <Zoom token={token} onClose={() => setIsZooming(false)} />
    }

    return (
        <Box
            sx={{
                display: 'flex',
                height: '100vh',
                flexDirection: 'column',
            }}
        >
            <Header />
            <Box
                sx={{
                    flex: 1,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <AnimatedBox
                    sx={{
                        perspective: 1000,
                    }}
                >
                    <AnimatedBox
                        sx={{
                            position: 'relative',
                            transformStyle: 'preserve-3d',
                            width: [280, 384],
                            height: [423, 567],

                            ':hover': {
                                transition: 'transform 0.1s',
                                transform: 'scale(1.02) translateZ(0)',
                            },
                        }}
                    >
                        <FrontCard
                            token={token}
                            flipVariants={flipVariants}
                            isCardFlipped={isCardFlipped}
                            setIsCardFlipped={setIsCardFlipped}
                            setIsZooming={setIsZooming}
                            isZooming={isZooming}
                        />
                        <BackCard
                            token={token}
                            flipVariants={flipVariants}
                            isCardFlipped={isCardFlipped}
                            setIsCardFlipped={setIsCardFlipped}
                        />
                    </AnimatedBox>
                </AnimatedBox>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: ['center', 'flex-end'],
                    p: 4,
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'auto auto',
                        gridGap: 2,
                    }}
                >
                    <Button
                        as="a"
                        variant="secondary"
                        target="_blank"
                        href={token.opensea_url}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                        }}
                        rel="noopener noreferrer"
                        download
                    >
                        <Box sx={{ display: 'inline-flex' }} mr={2}>
                            <OpenSeaIcon />
                        </Box>
                        <Box sx={{ display: ['none', 'inline'] }}>
                            View on&nbsp;
                        </Box>
                        OpenSea
                    </Button>
                    <Download token={token} />
                </Box>
            </Box>
        </Box>
    )
}

function BackCard({ flipVariants, isCardFlipped, setIsCardFlipped, token }) {
    const birthdayDate = Date.parse(token.birthday)
    const birthdayFormat = dateFormat(birthdayDate, 'mmm dS yyyy')

    const Item = ({ name, icon, value }) => {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Box sx={{ pr: 3, display: ['none', 'block'] }}>{icon}</Box>
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Text
                        sx={{
                            fontSize: [0, 1],
                            fontWeight: 'body',
                            textTransform: 'uppercase',
                        }}
                    >
                        {name}
                    </Text>
                    <Text sx={{ fontSize: [1, 2] }}>{value}</Text>
                </Box>
            </Box>
        )
    }
    return (
        <AnimatedCardFace
            token={token}
            variants={flipVariants}
            initial={{ rotateY: 180 }}
            animate={isCardFlipped ? 'shown' : 'backFlipped'}
            onClick={() => setIsCardFlipped(!isCardFlipped)}
        >
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                }}
            >
                <Heading sx={{ fontSize: [5, 6], mb: 4, maxWidth: '70%' }}>
                    Traits
                </Heading>

                <Grid columns={2} gap={[3, 4]}>
                    <Item
                        icon={<AtmosphereIcon />}
                        name="Atmosphere"
                        value={token.atmosphere}
                    />
                    <Item icon={<BodyIcon />} name="Body" value={token.body} />
                    <Item
                        icon={<ColorIcon />}
                        name="Color"
                        value={token.color}
                    />
                    <Item
                        icon={<EyeIcon />}
                        name="Eye color"
                        value={token.eye_color}
                    />
                    <Item
                        icon={<BirthdayIcon />}
                        name="Birthday"
                        value={birthdayFormat}
                    />
                    <Item
                        icon={<GlassesIcon />}
                        name="Glasses"
                        value={token.glasses}
                    />
                    <Item icon={<HatIcon />} name="Hat" value={token.hat} />
                    <Item
                        icon={<MouthIcon />}
                        name="Mouth"
                        value={token.mouth}
                    />
                    <Item icon={<FaceIcon />} name="Face" value={token.face} />
                </Grid>
                <Box sx={{ mb: 3, mt: 6 }}>
                    <CircleButton
                        icon={<RotateIcon />}
                        onClick={() => setIsCardFlipped(!isCardFlipped)}
                    />
                </Box>
            </Box>
        </AnimatedCardFace>
    )
}

function FrontCard({
    flipVariants,
    isCardFlipped,
    token,
    setIsCardFlipped,
    setIsZooming,
    isZooming,
}) {
    const directUrl = `${token.direct_url}?width=600&height=600&type=passport`

    return (
        <AnimatedCardFace
            token={token}
            variants={flipVariants}
            animate={isCardFlipped ? 'frontFlipped' : 'shown'}
            onClick={() => setIsCardFlipped(!isCardFlipped)}
        >
            <Box
                sx={{
                    position: 'absolute',
                    right: 2,
                    top: 2,
                    transform: 'rotate(20deg)',
                }}
            >
                <Box
                    sx={{
                        width: ['87px', '103px'],
                        height: ['70px', '83px'],
                        borderRadius: 2,
                    }}
                >
                    <Image
                        alt="Orange seal with text 'TINYFACE MINT APPROVED' and illustration of a face with glasses and mustache."
                        src="/pages/art/original-owner.png"
                        fill
                        sizes={206}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    position: 'absolute',
                    right: '5%',
                    top: '13%',
                    transform: 'rotate(-20deg)',
                }}
            >
                <Box
                    sx={{
                        borderRadius: [1, 2],
                        width: ['96px', '111px'],
                        height: ['74px', '86px'],
                    }}
                >
                    <Image
                        alt="Cyan geometric pattern with horizontal lines and central diamond shape."
                        src="/pages/art/tinyfaces.png"
                        fill
                        sizes={221}
                    />
                </Box>
            </Box>
            <Box
                sx={{
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'flex-end',
                }}
            >
                <Heading sx={{ fontSize: [6, 8], mt: 2, maxWidth: '70%' }}>
                    {token.name}
                </Heading>
                <Text
                    sx={{
                        textTransform: 'uppercase',
                        fontSize: [1, 2],
                        mb: [2, 3],
                        fontWeight: 'body',
                    }}
                >
                    token #{token.token_id}
                </Text>
                <Box sx={{ position: 'relative' }}>
                    <Grid
                        gap={1}
                        sx={{
                            position: 'absolute',
                            bottom: 2,
                            left: 2,
                            zIndex: 2,
                        }}
                    >
                        <CircleButton
                            icon={<ZoomIcon />}
                            onClick={(e) => {
                                e.stopPropagation()
                                setIsZooming(!isZooming)
                            }}
                        />
                        <CircleButton
                            icon={<RotateIcon />}
                            onClick={() => setIsCardFlipped(!isCardFlipped)}
                        />
                    </Grid>
                    <Box
                        sx={{
                            paddingBottom: '100%',
                            position: 'relative',
                            bg: 'white',
                            borderRadius: 2,
                            overflow: 'hidden',
                        }}
                    >
                        <ThemeImage
                            src={directUrl}
                            alt="TinyFaces NFT artwork where the face is zoomed in."
                            sx={{
                                width: '100%',
                                position: 'absolute',
                                left: 0,
                                top: 0,
                            }}
                            srcSet={`${directUrl} 1x, ${directUrl} 2x`}
                        />
                    </Box>
                </Box>
            </Box>
        </AnimatedCardFace>
    )
}

function CircleButton({ icon, onClick }) {
    return (
        <Box
            onClick={onClick}
            sx={{
                width: 36,
                height: 36,
                borderRadius: 18,
                display: 'flex',
                bg: 'secondary',
                color: 'background',
                justifyContent: 'center',
                alignItems: 'center',
            }}
        >
            {icon}
        </Box>
    )
}
function AnimatedCardFace({ children, token, ...rest }) {
    const color = backgroundTransformer(token.color)
    return (
        <AnimatedBox
            sx={{
                position: 'absolute',
                backfaceVisibility: 'hidden',
                height: '100%',
                overflow: 'hidden',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                background: color,
                borderRadius: [2, 3],
                cursor: 'pointer',
            }}
            {...rest}
        >
            <Box
                sx={{
                    position: 'relative',
                    flexDirection: 'column',
                    flex: 1,
                    display: 'flex',
                    px: [4, 5],
                    pb: [4, 5],
                    alignItems: 'flex-end',
                }}
            >
                <Box
                    sx={{
                        height: 12,
                        width: '26%',
                        borderRadius: 6,
                        mt: 3,
                        mx: 'auto',
                        bg: 'background',
                    }}
                ></Box>
                <Box sx={{ flex: 1, width: '100%' }}>{children}</Box>
            </Box>
        </AnimatedBox>
    )
}
